// Géométrie des îlots de marché (cadrage plan-precis-marches.md, D-300 à D-303) : découpage d'un îlot
// à quatre coins en places, numérotation îlot-rangée-rang, recalage par relevé terrain et rectangles
// orientés des places. Calculs dans un plan local en mètres (projection équirectangulaire autour du
// premier coin) : à l'échelle d'un marché (quelques centaines de mètres), l'écart est inférieur au
// centimètre. Aucune dépendance : partagé par l'API, les données fictives et l'interface.

export type Coord = [number, number];
export type Quadrilatere = [Coord, Coord, Coord, Coord];
type Xy = { x: number; y: number };

const RAYON = 6371008.8;
const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/** Plan local en mètres centré sur une origine (longitude, latitude). */
export function planLocal(origine: Coord) {
  const k = rad(1) * RAYON;
  const c = Math.cos(rad(origine[1]));
  return {
    versXy: (p: Coord): Xy => ({ x: (p[0] - origine[0]) * k * c, y: (p[1] - origine[1]) * k }),
    versCoord: (p: Xy): Coord => [origine[0] + p.x / (k * c), origine[1] + p.y / k],
  };
}

const arrondi = (n: number, d = 7) => Math.round(n * 10 ** d) / 10 ** d;
const dist = (a: Xy, b: Xy) => Math.hypot(b.x - a.x, b.y - a.y);

/** Distance en mètres entre deux coordonnées. */
export function distanceM(a: Coord, b: Coord): number {
  const p = planLocal(a);
  return dist(p.versXy(a), p.versXy(b));
}

function bilineaire(q: Xy[], u: number, v: number): Xy {
  const [a, b, c, d] = q as [Xy, Xy, Xy, Xy];
  return {
    x: (1 - u) * (1 - v) * a.x + u * (1 - v) * b.x + u * v * c.x + (1 - u) * v * d.x,
    y: (1 - u) * (1 - v) * a.y + u * (1 - v) * b.y + u * v * c.y + (1 - u) * v * d.y,
  };
}

/** Coordonnées (u, v) d'un point dans un quadrilatère (inverse bilinéaire par Newton). */
function inverseBilineaire(q: Xy[], p: Xy): { u: number; v: number } {
  const [a, b, c, d] = q as [Xy, Xy, Xy, Xy];
  let u = 0.5;
  let v = 0.5;
  for (let i = 0; i < 30; i++) {
    const f = bilineaire(q, u, v);
    const fx = f.x - p.x;
    const fy = f.y - p.y;
    const dux = (1 - v) * (b.x - a.x) + v * (c.x - d.x);
    const duy = (1 - v) * (b.y - a.y) + v * (c.y - d.y);
    const dvx = (1 - u) * (d.x - a.x) + u * (c.x - b.x);
    const dvy = (1 - u) * (d.y - a.y) + u * (c.y - b.y);
    const det = dux * dvy - dvx * duy;
    if (Math.abs(det) < 1e-12) break;
    const du = (fx * dvy - fy * dvx) / det;
    const dv = (fy * dux - fx * duy) / det;
    u -= du;
    v -= dv;
    if (Math.abs(du) + Math.abs(dv) < 1e-12) break;
  }
  return { u, v };
}

/** Aire signée (m²) d'un anneau en coordonnées locales. */
function aire(points: Xy[]): number {
  let s = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    s += a.x * b.y - b.x * a.y;
  }
  return s / 2;
}

/** Contrôle d'un îlot : quatre coins distincts formant un quadrilatère convexe non croisé. */
export function verifierQuadrilatere(coins: Coord[]): string | null {
  if (coins.length !== 4) return 'Un îlot se découpe à partir de quatre coins';
  const p = planLocal(coins[0]!);
  const q = coins.map(p.versXy);
  const signes = q.map((a, i) => {
    const b = q[(i + 1) % 4]!;
    const c = q[(i + 2) % 4]!;
    return (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
  });
  if (signes.some((s) => Math.abs(s) < 1e-6)) return 'Trois coins alignés ou confondus';
  if (!(signes.every((s) => s > 0) || signes.every((s) => s < 0)))
    return 'Les coins doivent former un quadrilatère convexe, dans l’ordre du contour';
  if (Math.abs(aire(q)) < 1) return 'Îlot de moins d’un mètre carré';
  return null;
}

export interface RegleNumerotation {
  format: 'ilot_rangee_rang' | 'ilot_continu';
  sens: 'meme_sens' | 'alterne';
  premier_numero: number;
  chiffres: number;
}

/** Rang affiché d'une place (1 à N) : sens alterné, les rangées paires sont numérotées à rebours. */
export function rangAffiche(rangee: number, rang: number, n: number, regle: RegleNumerotation) {
  return regle.sens === 'alterne' && rangee % 2 === 0 ? n - rang + 1 : rang;
}

/** Référence d'une place selon la règle de l'îlot : B-03-012 (îlot, rangée, rang) ou B-0123 (continu). */
export function referencePlace(
  code: string,
  rangee: number,
  rang: number,
  n: number,
  regle: RegleNumerotation,
): string {
  const r = rangAffiche(rangee, rang, n, regle);
  const pad = (v: number, l: number) => String(v).padStart(l, '0');
  if (regle.format === 'ilot_continu')
    return `${code}-${pad(regle.premier_numero + (rangee - 1) * n + r - 1, regle.chiffres)}`;
  return `${code}-${pad(rangee, 2)}-${pad(regle.premier_numero + r - 1, regle.chiffres)}`;
}

export interface FormePlace {
  centre: Coord;
  forme: Coord[];
  largeur_m: number;
  profondeur_m: number;
  /** Angle de la façade par rapport à l'est, en degrés, sens trigonométrique, dans [0, 360). */
  orientation_deg: number;
  surface_m2: number;
}

export interface PlaceDecoupee extends FormePlace {
  rangee: number;
  rang: number;
  reference: string;
}

function versForme(origine: Coord, cellule: Xy[]): FormePlace {
  const p = planLocal(origine);
  const [a, b, c, d] = cellule as [Xy, Xy, Xy, Xy];
  const centre = { x: (a.x + b.x + c.x + d.x) / 4, y: (a.y + b.y + c.y + d.y) / 4 };
  const largeur = (dist(a, b) + dist(d, c)) / 2;
  const profondeur = (dist(a, d) + dist(b, c)) / 2;
  const angle = (deg(Math.atan2(b.y - a.y + c.y - d.y, b.x - a.x + c.x - d.x)) + 360) % 360;
  const anneau = [a, b, c, d, a]
    .map((x) => p.versCoord(x))
    .map(([lo, la]) => [arrondi(lo), arrondi(la)] as Coord);
  const c2 = p.versCoord(centre);
  return {
    centre: [arrondi(c2[0]), arrondi(c2[1])],
    forme: anneau,
    largeur_m: Math.round(largeur * 100) / 100,
    profondeur_m: Math.round(profondeur * 100) / 100,
    orientation_deg: Math.round(angle * 100) / 100,
    surface_m2: Math.max(0.01, Math.round(Math.abs(aire([a, b, c, d])) * 100) / 100),
  };
}

/**
 * Découpe un îlot (coins A, B, C, D dans l'ordre du contour ; façade de la rangée 1 = A→B) en
 * rangées parallèles à la façade et en places égales le long de chaque rangée.
 */
export function decouperIlot(p: {
  code: string;
  coins: Coord[];
  rangees: number;
  places_par_rangee: number;
  numerotation: RegleNumerotation;
}): PlaceDecoupee[] {
  const probleme = verifierQuadrilatere(p.coins);
  if (probleme) throw new Error(probleme);
  const origine = p.coins[0]!;
  const local = planLocal(origine);
  const q = p.coins.map(local.versXy);
  const places: PlaceDecoupee[] = [];
  const n = p.places_par_rangee;
  for (let r = 1; r <= p.rangees; r++) {
    const v0 = (r - 1) / p.rangees;
    const v1 = r / p.rangees;
    for (let k = 1; k <= n; k++) {
      const u0 = (k - 1) / n;
      const u1 = k / n;
      const cellule = [
        bilineaire(q, u0, v0),
        bilineaire(q, u1, v0),
        bilineaire(q, u1, v1),
        bilineaire(q, u0, v1),
      ];
      places.push({
        rangee: r,
        rang: k,
        reference: referencePlace(p.code, r, k, n, p.numerotation),
        ...versForme(origine, cellule),
      });
    }
  }
  return places;
}

/** Façade (moyenne de A→B et D→C) et profondeur (moyenne de A→D et B→C) d'un îlot, en mètres. */
export function dimensionsIlot(coins: Coord[]): { facade_m: number; profondeur_m: number } {
  const p = planLocal(coins[0]!);
  const [a, b, c, d] = coins.map(p.versXy) as [Xy, Xy, Xy, Xy];
  return { facade_m: (dist(a, b) + dist(d, c)) / 2, profondeur_m: (dist(a, d) + dist(b, c)) / 2 };
}

/**
 * Recalage d'un îlot relevé sur le terrain : les coins moyennés donnent la position et l'orientation ;
 * la façade mesurée (au mètre ou au pas), plus fiable que le GPS, fixe la longueur le long de la
 * façade autour du centre ; la profondeur mesurée, si elle est fournie, fixe l'autre dimension.
 */
export function recalerCoins(
  coins: Coord[],
  mesures: { facade_m?: number; profondeur_m?: number },
): Quadrilatere {
  const p = planLocal(coins[0]!);
  const q = coins.map(p.versXy);
  const [a, b, c, d] = q as [Xy, Xy, Xy, Xy];
  const g = { x: q.reduce((s, x) => s + x.x, 0) / 4, y: q.reduce((s, x) => s + x.y, 0) / 4 };
  const ux0 = b.x - a.x + c.x - d.x;
  const uy0 = b.y - a.y + c.y - d.y;
  const nu = Math.hypot(ux0, uy0);
  const u = { x: ux0 / nu, y: uy0 / nu };
  const v = { x: -u.y, y: u.x };
  const dims = dimensionsIlot(coins);
  const fu = mesures.facade_m ? mesures.facade_m / dims.facade_m : 1;
  const fv = mesures.profondeur_m ? mesures.profondeur_m / dims.profondeur_m : 1;
  return q.map((x) => {
    const du = (x.x - g.x) * u.x + (x.y - g.y) * u.y;
    const dv = (x.x - g.x) * v.x + (x.y - g.y) * v.y;
    const r = p.versCoord({
      x: g.x + du * fu * u.x + dv * fv * v.x,
      y: g.y + du * fu * u.y + dv * fv * v.y,
    });
    return [arrondi(r[0]), arrondi(r[1])] as Coord;
  }) as Quadrilatere;
}

/** Transporte des points d'un îlot vers son nouveau contour (mêmes coordonnées relatives u, v). */
export function transporter(ancien: Coord[], nouveau: Coord[], points: Coord[]): Coord[] {
  const p = planLocal(ancien[0]!);
  const qa = ancien.map(p.versXy);
  const qn = nouveau.map(p.versXy);
  return points.map((x) => {
    const { u, v } = inverseBilineaire(qa, p.versXy(x));
    const r = p.versCoord(bilineaire(qn, u, v));
    return [arrondi(r[0]), arrondi(r[1])] as Coord;
  });
}

/** Rectangle orienté autour d'un centre. */
export function rectangle(
  centre: Coord,
  largeur: number,
  profondeur: number,
  orientationDeg: number,
): FormePlace {
  // Coins exprimés dans le plan local centré sur la place (origine = centre).
  const a = rad(orientationDeg);
  const u = { x: Math.cos(a), y: Math.sin(a) };
  const v = { x: -u.y, y: u.x };
  const coin = (su: number, sv: number): Xy => ({
    x: (su * largeur * u.x + sv * profondeur * v.x) / 2,
    y: (su * largeur * u.y + sv * profondeur * v.y) / 2,
  });
  return versForme(centre, [coin(-1, -1), coin(1, -1), coin(1, 1), coin(-1, 1)]);
}

/** Scission d'une place en parts égales le long de sa façade. */
export function scinder(
  f: Pick<FormePlace, 'centre' | 'largeur_m' | 'profondeur_m' | 'orientation_deg'>,
  parts: number,
): FormePlace[] {
  const p = planLocal(f.centre);
  const a = rad(f.orientation_deg);
  const l = f.largeur_m / parts;
  return Array.from({ length: parts }, (_, i) => {
    const decalage = -f.largeur_m / 2 + l * (i + 0.5);
    return rectangle(
      p.versCoord({ x: decalage * Math.cos(a), y: decalage * Math.sin(a) }),
      l,
      f.profondeur_m,
      f.orientation_deg,
    );
  });
}

/**
 * Fusion de places contiguës : rectangle englobant dans l'orientation de la première place. Refus
 * (null) si le rectangle obtenu dépasse de plus d'un quart la somme des surfaces (places disjointes).
 */
export function fusionner(
  formes: Pick<FormePlace, 'forme' | 'orientation_deg' | 'surface_m2'>[],
): FormePlace | null {
  const premiere = formes[0]!;
  const p = planLocal(premiere.forme[0]!);
  const a = rad(premiere.orientation_deg);
  const u = { x: Math.cos(a), y: Math.sin(a) };
  const v = { x: -u.y, y: u.x };
  const points = formes.flatMap((f) => f.forme.map(p.versXy));
  const us = points.map((x) => x.x * u.x + x.y * u.y);
  const vs = points.map((x) => x.x * v.x + x.y * v.y);
  const [u0, u1, v0, v1] = [Math.min(...us), Math.max(...us), Math.min(...vs), Math.max(...vs)];
  const somme = formes.reduce((s, f) => s + f.surface_m2, 0);
  if ((u1 - u0) * (v1 - v0) > somme * 1.25 + 0.5) return null;
  const centre = p.versCoord({
    x: ((u0 + u1) / 2) * u.x + ((v0 + v1) / 2) * v.x,
    y: ((u0 + u1) / 2) * u.y + ((v0 + v1) / 2) * v.y,
  });
  return rectangle(centre, u1 - u0, v1 - v0, premiere.orientation_deg);
}

/** Translation d'une forme vers un nouveau centre. */
export function translater(forme: Coord[], ancienCentre: Coord, nouveauCentre: Coord): Coord[] {
  const p = planLocal(ancienCentre);
  const d = p.versXy(nouveauCentre);
  return forme.map((x) => {
    const l = p.versXy(x);
    const r = p.versCoord({ x: l.x + d.x, y: l.y + d.y });
    return [arrondi(r[0]), arrondi(r[1])] as Coord;
  });
}

/** Rectangle orienté d'aire minimale contenant un anneau (pour un îlot ou une place importés). */
export function rectangleMinimal(anneau: Coord[]): { coins: Quadrilatere; forme: FormePlace } {
  const ouvert =
    anneau.length > 1 && anneau[0]![0] === anneau.at(-1)![0] && anneau[0]![1] === anneau.at(-1)![1]
      ? anneau.slice(0, -1)
      : anneau;
  const p = planLocal(ouvert[0]!);
  const pts = ouvert.map(p.versXy);
  let meilleur: {
    aire: number;
    angle: number;
    u0: number;
    u1: number;
    v0: number;
    v1: number;
  } | null = null;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    if (dist(a, b) < 1e-6) continue;
    const angle = Math.atan2(b.y - a.y, b.x - a.x);
    const u = { x: Math.cos(angle), y: Math.sin(angle) };
    const us = pts.map((x) => x.x * u.x + x.y * u.y);
    const vs = pts.map((x) => -x.x * u.y + x.y * u.x);
    const r = {
      angle,
      u0: Math.min(...us),
      u1: Math.max(...us),
      v0: Math.min(...vs),
      v1: Math.max(...vs),
    };
    const s = (r.u1 - r.u0) * (r.v1 - r.v0);
    if (!meilleur || s < meilleur.aire - 1e-9) meilleur = { aire: s, ...r };
  }
  if (!meilleur) throw new Error('Contour dégénéré');
  const { angle, u0, u1, v0, v1 } = meilleur;
  const u = { x: Math.cos(angle), y: Math.sin(angle) };
  const v = { x: -u.y, y: u.x };
  const coin = (cu: number, cv: number) => {
    const r = p.versCoord({ x: cu * u.x + cv * v.x, y: cu * u.y + cv * v.y });
    return [arrondi(r[0]), arrondi(r[1])] as Coord;
  };
  const coins: Quadrilatere = [coin(u0, v0), coin(u1, v0), coin(u1, v1), coin(u0, v1)];
  return { coins, forme: versForme(coins[0], coins.map(planLocal(coins[0]).versXy)) };
}

/** Moyenne de positions relevées (coin d'îlot moyenné sur 30 s) et écart type en mètres. */
export function moyennePositions(points: Coord[]): { position: Coord; ecart_m: number } {
  const m: Coord = [
    points.reduce((s, x) => s + x[0], 0) / points.length,
    points.reduce((s, x) => s + x[1], 0) / points.length,
  ];
  const p = planLocal(m);
  const ecart = Math.sqrt(
    points.reduce((s, x) => s + dist(p.versXy(x), { x: 0, y: 0 }) ** 2, 0) / points.length,
  );
  return { position: [arrondi(m[0]), arrondi(m[1])], ecart_m: Math.round(ecart * 10) / 10 };
}
